/**
 * MCP Query Tools
 *
 * Tools for searching, filtering, and analyzing IDF model data.
 */

import type { MCPTool } from '../types';

/**
 * Search objects
 */
const searchObjects: MCPTool = {
  name: 'search_objects',
  description: `Search for objects by text query. Searches object names, class names, and field values. Returns matching objects ranked by relevance.`,
  inputSchema: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'The search query text',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of results (default: 20)',
        default: 20,
      },
      classFilter: {
        type: 'string',
        description: 'Optional: filter results to specific class name',
      },
    },
    required: ['query'],
  },
};

/**
 * Find references to an object
 */
const findReferences: MCPTool = {
  name: 'find_references',
  description: `Find all objects that reference a given object by name. Useful before renaming or removing an object to understand impact.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectName: {
        type: 'string',
        description: 'The name of the object to find references to',
      },
    },
    required: ['objectName'],
  },
};

/**
 * Find objects referencing a value
 */
const findObjectsWithValue: MCPTool = {
  name: 'find_objects_with_value',
  description: `Find all objects that contain a specific value in any field. Useful for finding usage of schedules, materials, etc.`,
  inputSchema: {
    type: 'object',
    properties: {
      value: {
        type: 'string',
        description: 'The value to search for',
      },
      exactMatch: {
        type: 'boolean',
        description: 'Whether to require exact match (default: false for contains)',
        default: false,
      },
    },
    required: ['value'],
  },
};

/**
 * Get objects by zone
 */
const getObjectsByZone: MCPTool = {
  name: 'get_objects_by_zone',
  description: `Get all objects associated with a specific zone including surfaces, people, lights, equipment, and HVAC components.`,
  inputSchema: {
    type: 'object',
    properties: {
      zoneName: {
        type: 'string',
        description: 'The zone name',
      },
    },
    required: ['zoneName'],
  },
};

/**
 * Get connected objects
 */
const getConnectedObjects: MCPTool = {
  name: 'get_connected_objects',
  description: `Get all objects that are connected to a given object through references. Shows the object relationship graph.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The starting object ID',
      },
      depth: {
        type: 'number',
        description: 'How many levels of connections to traverse (default: 1)',
        default: 1,
      },
    },
    required: ['objectId'],
  },
};

/**
 * Compare objects
 */
const compareObjects: MCPTool = {
  name: 'compare_objects',
  description: `Compare two objects of the same class and show the differences in their field values.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId1: {
        type: 'string',
        description: 'First object ID',
      },
      objectId2: {
        type: 'string',
        description: 'Second object ID',
      },
    },
    required: ['objectId1', 'objectId2'],
  },
};

/**
 * Get objects with validation errors
 */
const getObjectsWithErrors: MCPTool = {
  name: 'get_objects_with_errors',
  description: `Get all objects that have validation errors or warnings. Useful for finding and fixing model issues.`,
  inputSchema: {
    type: 'object',
    properties: {
      severityFilter: {
        type: 'string',
        description: 'Filter by severity: "error", "warning", or "all" (default)',
        enum: ['error', 'warning', 'all'],
        default: 'all',
      },
    },
  },
};

/**
 * Get orphaned objects
 */
const getOrphanedObjects: MCPTool = {
  name: 'get_orphaned_objects',
  description: `Find objects that are defined but not referenced by any other object. These may be unused and safe to remove.`,
  inputSchema: {
    type: 'object',
    properties: {
      classFilter: {
        type: 'string',
        description: 'Optional: only check specific class (e.g., "Schedule:Compact")',
      },
    },
  },
};

/**
 * Get duplicate objects
 */
const getDuplicateObjects: MCPTool = {
  name: 'get_duplicate_objects',
  description: `Find objects that have the same name, which may cause simulation errors.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Filter objects by field value
 */
const filterObjects: MCPTool = {
  name: 'filter_objects',
  description: `Filter objects by field value conditions. Supports numeric comparisons and string matching.`,
  inputSchema: {
    type: 'object',
    properties: {
      className: {
        type: 'string',
        description: 'The object class to filter',
      },
      conditions: {
        type: 'array',
        description: 'Array of filter conditions',
        items: {
          type: 'object',
          properties: {
            fieldIndex: {
              type: 'number',
              description: 'The field index to check',
            },
            operator: {
              type: 'string',
              description: 'Comparison operator',
              enum: ['equals', 'notEquals', 'contains', 'greaterThan', 'lessThan', 'between'],
            },
            value: {
              type: 'string',
              description: 'The value to compare against',
            },
            value2: {
              type: 'string',
              description: 'Second value for "between" operator',
            },
          },
        },
      },
    },
    required: ['className', 'conditions'],
  },
};

export const queryTools: MCPTool[] = [
  searchObjects,
  findReferences,
  findObjectsWithValue,
  getObjectsByZone,
  getConnectedObjects,
  compareObjects,
  getObjectsWithErrors,
  getOrphanedObjects,
  getDuplicateObjects,
  filterObjects,
];
